import {
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { DOWNTIMEMETHODCAPTURE } from "constants/constants";
import { onlyNumbersRegex } from "validations/matchers";
import { useStyles } from "../MachineCenterCreationForm.styles";
import type { IMachineCenterCreationFormValues } from "../MachineCenterCreationFormInterfaces";
import { IMachineCenterToDuplicate } from "../../../components/ProductionLineAccordion/ProductionLineAccordionInterfaces";
import { ChangeEvent } from "react";

export const GENERAL_TAB_FIELD_NAMES: Array<keyof IMachineCenterCreationFormValues> = [
    "name",
    "short_name",
    "downtime_method_capture_id",
    "plc_address",
    "minimum_downtime",
    "downtime_trigger_threshold",
    "production_line_id_send_duplicate",
    "production_line_id_to_duplicate",
    "machine_center_id_to_duplicate",
];

interface GeneralTabProps {
    values: IMachineCenterCreationFormValues;
    errors: Record<string, any>;
    touched: Record<string, any>;
    handleChange: {
        (e: React.ChangeEvent<any>): void;
        <T = string | ChangeEvent<any>>(
            field: T,
        ): T extends React.ChangeEvent<any> ? void : (e: string | React.ChangeEvent<any>) => void;
    };
    setFieldValue: (field: string, value: any) => void;
    t: (key: string) => string;
    isEdit?: boolean;
    isDuplicationWithSelection: boolean;
    duplicationInCreation: boolean;
    setDuplicationInCreation: (value: boolean) => void;
    duplicateMachineCenterInfo?: IMachineCenterToDuplicate;
    productionLineOptions: any[];
    downtimeMethodCaptures: any[];
    isMillAspenPlusEnabled: boolean;
    activeTab: number;
    setActiveTab: (tab: number) => void;
    setMachineCenterIdSelected: (id: number | undefined) => void;
}

export const GeneralTab = ({
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    t,
    isEdit,
    isDuplicationWithSelection,
    duplicationInCreation,
    setDuplicationInCreation,
    duplicateMachineCenterInfo,
    productionLineOptions,
    downtimeMethodCaptures,
    isMillAspenPlusEnabled,
    activeTab,
    setActiveTab,
    setMachineCenterIdSelected,
}: GeneralTabProps) => {
    const classes = useStyles();

    return (
        <Box className={classes.tabPanel}>
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Box className={classes.boxContainer}>
                        <InputLabel id="outlined-name-label">
                            <Typography variant="subtitle2" className={classes.formLabel}>
                                {t("corporate_backoffice.production_line_view.machine_center_creation_form.short_name")}
                            </Typography>
                        </InputLabel>
                        <TextField
                            aria-labelledby="outlined-short-name-label"
                            fullWidth
                            id="outlined-short-name-input"
                            className={classes.textField}
                            name="short_name"
                            type="text"
                            variant="outlined"
                            value={values.short_name}
                            onChange={handleChange}
                            error={touched.short_name && Boolean(errors.short_name)}
                            helperText={touched.short_name && errors.short_name && t(errors.short_name)}
                        />
                    </Box>
                </Grid>
                <Grid item xs={8}>
                    <Box className={classes.boxContainer}>
                        <InputLabel id="outlined-name-label">
                            <Typography variant="subtitle2" className={classes.formLabel}>
                                {t("corporate_backoffice.production_line_view.machine_center_creation_form.name")}
                            </Typography>
                        </InputLabel>
                        <TextField
                            aria-labelledby="outlined-name-label"
                            fullWidth
                            id="outlined-name-input"
                            className={classes.textField}
                            name="name"
                            type="text"
                            variant="outlined"
                            value={values.name}
                            onChange={handleChange}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name && t(errors.name)}
                        />
                    </Box>
                </Grid>
            </Grid>
            {isDuplicationWithSelection && !isEdit && (
                <Box className={classes.boxContainer}>
                    <InputLabel id="outlined-name-label">
                        <Typography variant="subtitle2" className={classes.formLabel}>
                            {t(
                                "corporate_backoffice.production_line_view.machine_center_creation_form.production_line",
                            )}
                        </Typography>
                    </InputLabel>
                    <FormControl
                        error={
                            touched.production_line_id_send_duplicate &&
                            Boolean(errors.production_line_id_send_duplicate)
                        }
                        className={classes.textField}
                        fullWidth
                    >
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            className={classes.select}
                            name="production_line_id_send_duplicate"
                            value={values.production_line_id_send_duplicate}
                            onChange={handleChange}
                        >
                            {duplicateMachineCenterInfo?.productionLineOptions.map((pl) => (
                                <MenuItem
                                    key={pl.id}
                                    value={pl.id}
                                    onClick={() => {
                                        setFieldValue("production_line_id_send_duplicate", pl.id);
                                    }}
                                >
                                    {pl.name}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {touched.production_line_id_send_duplicate &&
                                errors.production_line_id_send_duplicate &&
                                t(errors.production_line_id_send_duplicate)}
                        </FormHelperText>
                    </FormControl>
                </Box>
            )}
            <Grid className={classes.boxContainerSeparator} container spacing={2}>
                <Grid item xs={6}>
                    <Box className={classes.boxContainer}>
                        <InputLabel id="outlined-name-label">
                            <Typography variant="subtitle2" className={classes.formLabel}>
                                {t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.manufacturer",
                                )}
                            </Typography>
                        </InputLabel>
                        <TextField
                            aria-labelledby="outlined-manufacturer-label"
                            fullWidth
                            id="outlined-manufacturer-input"
                            className={classes.textField}
                            name="manufacturer"
                            type="text"
                            variant="outlined"
                            value={values.manufacturer}
                            onChange={handleChange}
                            error={touched.manufacturer && Boolean(errors.manufacturer)}
                            helperText={touched.manufacturer && errors.manufacturer && t(errors.manufacturer)}
                        />
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box className={classes.boxContainer}>
                        <InputLabel id="outlined-name-label">
                            <Typography variant="subtitle2" className={classes.formLabel}>
                                {t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.model_number",
                                )}
                            </Typography>
                        </InputLabel>
                        <TextField
                            aria-labelledby="outlined-model-number-label"
                            fullWidth
                            id="outlined-model-number-input"
                            className={classes.textField}
                            name="model_number"
                            type="text"
                            variant="outlined"
                            value={values.model_number}
                            onChange={handleChange}
                            error={touched.model_number && Boolean(errors.model_number)}
                            helperText={touched.model_number && errors.model_number && t(errors.model_number)}
                        />
                    </Box>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Box className={classes.boxContainer}>
                        <InputLabel id="outlined-name-label">
                            <Typography variant="subtitle2" className={classes.formLabel}>
                                {t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.downtime_method_capture",
                                )}
                            </Typography>
                        </InputLabel>
                        <FormControl
                            error={touched.downtime_method_capture_id && Boolean(errors.downtime_method_capture_id)}
                            className={classes.textField}
                            fullWidth
                        >
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                className={classes.select}
                                name="downtime_method_capture_id"
                                value={values.downtime_method_capture_id}
                                onChange={handleChange}
                            >
                                {downtimeMethodCaptures.map((downtime_method_capture, index) => (
                                    <MenuItem
                                        key={index}
                                        value={downtime_method_capture.downtime_method_capture_id}
                                        onClick={() => {
                                            setFieldValue(
                                                "downtime_method_capture_id",
                                                downtime_method_capture.downtime_method_capture_id,
                                            );
                                        }}
                                    >
                                        {downtime_method_capture.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                {touched.downtime_method_capture_id &&
                                    errors.downtime_method_capture_id &&
                                    t(errors.downtime_method_capture_id)}
                            </FormHelperText>
                        </FormControl>
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box className={classes.boxContainer}>
                        <InputLabel id="outlined-name-label">
                            <Typography variant="subtitle2" className={classes.formLabel}>
                                {t(
                                    values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC
                                        ? "corporate_backoffice.production_line_view.machine_center_creation_form.plc_address_required"
                                        : "corporate_backoffice.production_line_view.machine_center_creation_form.plc_address",
                                )}
                            </Typography>
                        </InputLabel>
                        <TextField
                            aria-labelledby="outlined-plc-address-label"
                            fullWidth
                            id="outlined-plc-address-input"
                            className={classes.textField}
                            name="plc_address"
                            disabled={!(values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC)}
                            type="text"
                            variant="outlined"
                            value={
                                values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC
                                    ? values.plc_address
                                    : ""
                            }
                            onChange={handleChange}
                            error={touched.plc_address && Boolean(errors.plc_address)}
                            helperText={touched.plc_address && errors.plc_address && t(errors.plc_address)}
                        />
                    </Box>
                </Grid>
            </Grid>
            <Grid className={classes.boxContainerSeparator} container spacing={2}>
                <Grid item xs={6}>
                    <Box className={classes.boxContainer}>
                        <InputLabel id="outlined-minimum-downtime-label">
                            <Typography variant="subtitle2" className={classes.formLabel}>
                                {t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.minimum_downtime_definition",
                                )}
                            </Typography>
                        </InputLabel>
                        <TextField
                            aria-labelledby="outlined-minimum-downtime-label"
                            fullWidth
                            id="outlined-minimum-downtime-input"
                            className={classes.textField}
                            placeholder={t(
                                "corporate_backoffice.production_line_view.machine_center_creation_form.minimum_downtime_definition_placeholder",
                            )}
                            name="minimum_downtime"
                            type="text"
                            variant="outlined"
                            value={values.minimum_downtime}
                            onChange={(e) => {
                                if (e.target.value === "" || onlyNumbersRegex.test(e.target.value)) {
                                    handleChange(e);
                                }
                            }}
                            error={touched.minimum_downtime && Boolean(errors.minimum_downtime)}
                            helperText={
                                touched.minimum_downtime && errors.minimum_downtime && t(errors.minimum_downtime)
                            }
                        />
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box className={classes.boxContainer}>
                        <InputLabel id="outlined-downtime-trigger-threshold-label">
                            <Typography variant="subtitle2" className={classes.formLabel}>
                                {t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.downtime_trigger",
                                )}
                            </Typography>
                        </InputLabel>
                        <TextField
                            aria-labelledby="outlined-downtime-trigger-threshold-label"
                            fullWidth
                            id="outlined-downtime-trigger-threshold-input"
                            className={classes.textField}
                            placeholder={t(
                                "corporate_backoffice.production_line_view.machine_center_creation_form.downtime_trigger_placeholder",
                            )}
                            name="downtime_trigger_threshold"
                            type="text"
                            variant="outlined"
                            value={values.downtime_trigger_threshold}
                            onChange={(e) => {
                                if (e.target.value === "" || onlyNumbersRegex.test(e.target.value)) {
                                    handleChange(e);
                                }
                            }}
                            error={touched.downtime_trigger_threshold && Boolean(errors.downtime_trigger_threshold)}
                            helperText={
                                touched.downtime_trigger_threshold &&
                                errors.downtime_trigger_threshold &&
                                t(errors.downtime_trigger_threshold)
                            }
                        />
                    </Box>
                </Grid>
            </Grid>
            <Box className={classes.boxContainer}>
                <FormControlLabel
                    className={classes.switchLabel}
                    value="start"
                    control={
                        <Switch
                            className={classes.switch}
                            checked={values.is_bottleneck}
                            onChange={(e) => setFieldValue("is_bottleneck", e.target.checked)}
                        />
                    }
                    label={t("corporate_backoffice.production_line_view.machine_center_creation_form.bottleneck")}
                    labelPlacement="start"
                />
                {isMillAspenPlusEnabled && (
                    <FormControlLabel
                        className={classes.switchLabel}
                        value="start"
                        control={
                            <Switch
                                className={classes.switch}
                                checked={values.aspen_plus_oee_enabled}
                                onChange={(e) => {
                                    const enabled = e.target.checked;
                                    setFieldValue("aspen_plus_oee_enabled", enabled);
                                    if (!enabled) {
                                        setFieldValue("oee_input_method_id", "");
                                        if (activeTab === 1) {
                                            setActiveTab(0);
                                        }
                                    } else if (isMillAspenPlusEnabled) {
                                        setActiveTab(1);
                                    }
                                }}
                            />
                        }
                        label={t(
                            "corporate_backoffice.production_line_view.machine_center_creation_form.aspen_plus_oee",
                        )}
                        labelPlacement="start"
                    />
                )}
                {!isDuplicationWithSelection && !isEdit && (
                    <FormControlLabel
                        className={classes.switchLabel}
                        value="start"
                        control={
                            <Switch
                                className={classes.switch}
                                checked={values.is_duplication_without_selection}
                                onChange={(e) => {
                                    setDuplicationInCreation(e.target.checked);
                                    setFieldValue("is_duplication_without_selection", e.target.checked);
                                }}
                            />
                        }
                        label={t("corporate_backoffice.production_line_view.machine_center_creation_form.duplication")}
                        labelPlacement="start"
                    />
                )}
            </Box>
            {!isDuplicationWithSelection && !isEdit && duplicationInCreation && (
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t(
                                        "corporate_backoffice.production_line_view.machine_center_creation_form.production_line",
                                    )}
                                </Typography>
                            </InputLabel>
                            <FormControl
                                error={
                                    touched.production_line_id_to_duplicate &&
                                    Boolean(errors.production_line_id_to_duplicate)
                                }
                                className={classes.textField}
                                fullWidth
                            >
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    className={classes.select}
                                    name="production_line_id_to_duplicate"
                                    value={values.production_line_id_to_duplicate}
                                    onChange={handleChange}
                                >
                                    {productionLineOptions.map((pl) => (
                                        <MenuItem
                                            key={pl.production_line_id}
                                            value={pl.production_line_id}
                                            onClick={() => {
                                                setFieldValue("production_line_id_to_duplicate", pl.production_line_id);
                                            }}
                                        >
                                            {pl.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {touched.production_line_id_to_duplicate &&
                                        errors.production_line_id_to_duplicate &&
                                        t(errors.production_line_id_to_duplicate)}
                                </FormHelperText>
                            </FormControl>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t(
                                        "corporate_backoffice.production_line_view.machine_center_creation_form.machine_center",
                                    )}
                                </Typography>
                            </InputLabel>
                            <FormControl
                                error={
                                    touched.machine_center_id_to_duplicate &&
                                    Boolean(errors.machine_center_id_to_duplicate)
                                }
                                className={classes.textField}
                                fullWidth
                            >
                                <Select
                                    disabled={!values.production_line_id_to_duplicate}
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    className={classes.select}
                                    name="machine_center_id_to_duplicate"
                                    value={values.machine_center_id_to_duplicate}
                                    onChange={handleChange}
                                >
                                    {productionLineOptions
                                        .find((pl) => pl.production_line_id === values.production_line_id_to_duplicate)
                                        ?.machine_centers?.map((mc: any) => (
                                            <MenuItem
                                                key={mc.machine_center_id}
                                                value={mc.machine_center_id}
                                                onClick={() => {
                                                    setMachineCenterIdSelected(mc.machine_center_id);
                                                    setFieldValue(
                                                        "machine_center_id_to_duplicate",
                                                        mc.machine_center_id,
                                                    );
                                                }}
                                            >
                                                {mc.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                                <FormHelperText>
                                    {touched.machine_center_id_to_duplicate &&
                                        errors.machine_center_id_to_duplicate &&
                                        t(errors.machine_center_id_to_duplicate)}
                                </FormHelperText>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};
